import {
  FLOW_BRANCHING_NODE_TYPES,
  FLOW_CONDITION_MAX_DEPTH,
  FLOW_DOMAIN_EVENT_TRIGGERS,
  FLOW_LIST_OPERATORS,
  FLOW_LOOP_LIMIT_MAX,
  FLOW_LOOP_LIMIT_MIN,
  FLOW_MAX_SUBFLOW_DEPTH,
  FLOW_REPLY_POLICIES,
  FLOW_SPLIT_TOTAL_WEIGHT,
  FLOW_VALUELESS_OPERATORS,
  SIMPLE_FLOW_ALLOWED_NODE_TYPES,
  flowNodeOutputPorts,
  isFlowTerminalNode,
  type FlowCondition,
  type FlowConditionLiteral,
  type FlowConditionOperator,
  type FlowDependencyRef,
  type FlowDraft,
  type FlowEdge,
  type FlowExecutionMode,
  type FlowIssueSeverity,
  type FlowNode,
  type FlowValidationCode,
  type FlowValidationIssue,
  type FlowValidationResult,
  type FlowValueType,
  type FlowVersion,
  type FunnelStructureType,
} from "./contracts";
import {
  flowConditionComparisons,
  flowConditionDepth,
  isOperatorAllowed,
  operandKey,
  operandValueType,
} from "./conditions";
import {
  buildFlowGraphIndex,
  findFlowCycles,
  outgoingEdges,
  reachableFrom,
  reachesTerminal,
} from "./graph";
import type {
  ApproachType,
  ReplyClassification,
} from "../ai-messages/contracts";

/**
 * Validador estruturado da definição de flows.
 *
 * Ele ACUMULA problemas em vez de parar no primeiro erro, separa `error` de
 * `warning` e devolve código estável, mensagem em português, alvo afetado e
 * ação recomendada. Nada aqui publica, compila ou executa.
 */

export interface FlowCatalogEntry {
  id: string;
  ownerId: string;
  funnelId?: string;
  archived?: boolean;
}

export interface FlowCatalogFunnel extends FlowCatalogEntry {
  structureType: FunnelStructureType;
  /** Primeiro nível da hierarquia de modo: funil → flow → passo. */
  defaultExecutionMode?: FlowExecutionMode;
}

export interface FlowCatalogPublishedArtifact extends FlowCatalogEntry {
  version: number;
  status: "draft" | "published";
  /** Modelo/pai do artefato versionado, quando existir. */
  parentId?: string;
  /** Valores congelados pelo catálogo, quando ele é usado em uma condição. */
  allowedValues?: readonly string[];
}

export interface FlowDependencyCatalog {
  ownerId: string;
  funnels: FlowCatalogFunnel[];
  stages: FlowCatalogEntry[];
  leadFields: FlowCatalogEntry[];
  tags: FlowCatalogEntry[];
  triggers: FlowCatalogEntry[];
  lossReasons: FlowCatalogEntry[];
  messageTemplateVersions: FlowCatalogPublishedArtifact[];
  aiPromptVersions: FlowCatalogPublishedArtifact[];
  replyClassificationVersions: FlowCatalogPublishedArtifact[];
  approachTypeVersions: FlowCatalogPublishedArtifact[];
  flowVersions: FlowVersion[];
  /** IDs de rascunhos conhecidos: referenciar um deles é erro de publicação. */
  flowDraftIds: string[];
}

export interface FlowCatalogSource {
  ownerId: string;
  funnels: {
    id: string;
    structureType?: FunnelStructureType;
    defaultExecutionMode?: FlowExecutionMode;
    archived?: boolean;
  }[];
  stages: { id: string; funnelId: string; archived?: boolean }[];
  leadFields: { id: string; funnelId: string; archived?: boolean }[];
  tags: { id: string; funnelId: string; archived?: boolean }[];
  triggers: { id: string; funnelId: string; archived?: boolean }[];
  lossReasons: { id: string; funnelId: string; archived?: boolean }[];
  messageTemplateVersions: {
    id: string;
    ownerId: string;
    templateId: string;
    version: number;
    status: "draft" | "published";
  }[];
  aiPromptVersions: { id: string; ownerId: string; version: number; status: "draft" | "published" }[];
  replyClassificationVersions: {
    id: string;
    ownerId: string;
    version: number;
    status: "draft" | "published";
    classifications: ReplyClassification[];
  }[];
  approachTypeVersions: {
    id: string;
    ownerId: string;
    version: number;
    status: "draft" | "published";
    approaches: ApproachType[];
  }[];
  flowVersions: FlowVersion[];
  flowDrafts: { id: string }[];
}

/** Catálogo autorizado de dependências, sempre escopado ao proprietário. */
export function buildFlowDependencyCatalog(source: FlowCatalogSource): FlowDependencyCatalog {
  const owned = <T extends { id: string; funnelId?: string; archived?: boolean }>(items: T[]) =>
    items.map((item) => ({ ...item, ownerId: source.ownerId }));
  return {
    ownerId: source.ownerId,
    funnels: source.funnels.map((funnel) => ({
      id: funnel.id,
      ownerId: source.ownerId,
      structureType: funnel.structureType ?? "simple",
      defaultExecutionMode: funnel.defaultExecutionMode,
      archived: funnel.archived,
    })),
    stages: owned(source.stages),
    leadFields: owned(source.leadFields),
    tags: owned(source.tags),
    triggers: owned(source.triggers),
    lossReasons: owned(source.lossReasons),
    messageTemplateVersions: source.messageTemplateVersions.map((item) => ({
      id: item.id,
      ownerId: item.ownerId,
      version: item.version,
      status: item.status,
      parentId: item.templateId,
    })),
    aiPromptVersions: source.aiPromptVersions.map((item) => ({ ...item })),
    replyClassificationVersions: source.replyClassificationVersions.map((item) => ({
      id: item.id,
      ownerId: item.ownerId,
      version: item.version,
      status: item.status,
      allowedValues: [...item.classifications],
    })),
    approachTypeVersions: source.approachTypeVersions.map((item) => ({
      id: item.id,
      ownerId: item.ownerId,
      version: item.version,
      status: item.status,
      allowedValues: item.approaches.map((approach) => approach.id),
    })),
    flowVersions: source.flowVersions,
    flowDraftIds: source.flowDrafts.map((draft) => draft.id),
  };
}

interface IssueInput {
  code: FlowValidationCode;
  severity: FlowIssueSeverity;
  message: string;
  recommendedAction: string;
  nodeId?: string;
  edgeId?: string;
  dependency?: FlowDependencyRef;
}

class IssueCollector {
  private readonly items: FlowValidationIssue[] = [];

  add(input: IssueInput): void {
    this.items.push({ ...input });
  }

  error(input: Omit<IssueInput, "severity">): void {
    this.add({ ...input, severity: "error" });
  }

  warning(input: Omit<IssueInput, "severity">): void {
    this.add({ ...input, severity: "warning" });
  }

  result(): FlowValidationResult {
    const errors = this.items.filter((issue) => issue.severity === "error");
    const warnings = this.items.filter((issue) => issue.severity === "warning");
    return { ok: errors.length === 0, issues: [...this.items], errors, warnings };
  }
}

// ---------------------------------------------------------------------------
// Extração de dependências
// ---------------------------------------------------------------------------

function conditionDependencies(condition: FlowCondition, funnelId: string): FlowDependencyRef[] {
  const refs: FlowDependencyRef[] = [];
  for (const comparison of flowConditionComparisons(condition)) {
    const { operand, value } = comparison;
    if (operand.kind === "custom_field")
      refs.push({ type: "lead_field", id: operand.fieldId, funnelId });
    if (operand.kind === "reply_classification")
      refs.push({
        type: "reply_classification_version",
        id: operand.catalogVersionId,
        version: operand.catalogVersion,
      });
    if (operand.kind === "approach_type")
      refs.push({
        type: "approach_type_version",
        id: operand.catalogVersionId,
        version: operand.catalogVersion,
      });
    if (operand.kind === "related_membership")
      refs.push({ type: "funnel", id: operand.funnelId });
    if (operand.kind === "stage") {
      const ids = Array.isArray(value) ? value : value === undefined ? [] : [value];
      for (const id of ids) refs.push({ type: "stage", id: String(id), funnelId });
    }
    if (operand.kind === "tag" || operand.kind === "trigger") {
      const ids = Array.isArray(value) ? value : value === undefined ? [] : [value];
      for (const id of ids)
        refs.push({ type: operand.kind === "tag" ? "tag" : "trigger", id: String(id), funnelId });
    }
  }
  return refs;
}

/** Todas as dependências citadas por um rascunho, em ordem canônica e sem repetição. */
export function collectFlowDependencies(draft: FlowDraft): FlowDependencyRef[] {
  const refs: FlowDependencyRef[] = [{ type: "funnel", id: draft.funnelId }];
  for (const node of draft.nodes) {
    const config = node.config;
    // A etapa declarada no passo é dependência como qualquer outra.
    if (node.stageId) refs.push({ type: "stage", id: node.stageId, funnelId: draft.funnelId });
    if (config.type === "stage") refs.push({ type: "stage", id: config.stageId, funnelId: draft.funnelId });
    if (config.type === "lost") {
      refs.push({ type: "loss_reason", id: config.lossReasonId, funnelId: draft.funnelId });
      refs.push({ type: "stage", id: config.stageId, funnelId: draft.funnelId });
    }
    if (config.type === "send_message")
      refs.push({
        type: "message_template_version",
        id: config.templateVersionId,
        version: config.templateVersion,
      });
    if (config.type === "ai_response")
      refs.push({
        type: "ai_prompt_version",
        id: config.promptVersionId,
        version: config.promptVersion,
      });
    if (config.type === "set_field_or_tag") {
      for (const assignment of config.assignments) {
        const type =
          assignment.target === "lead_field"
            ? "lead_field"
            : assignment.target === "tag"
              ? "tag"
              : "trigger";
        refs.push({ type, id: assignment.id, funnelId: draft.funnelId });
      }
    }
    if (config.type === "call_flow") {
      refs.push({
        type: "flow_version",
        id: config.target.flowVersionId,
        version: config.target.flowVersion,
      });
      if (config.target.scope === "other_funnel")
        refs.push({ type: "funnel", id: config.target.funnelId });
    }
  }
  for (const edge of draft.edges) {
    if (edge.condition) refs.push(...conditionDependencies(edge.condition, draft.funnelId));
  }
  const unique = new Map<string, FlowDependencyRef>();
  for (const ref of refs) unique.set(`${ref.type}:${ref.id}:${ref.version ?? ""}`, ref);
  return [...unique.values()].sort((left, right) => {
    if (left.type !== right.type) return left.type.localeCompare(right.type);
    if (left.id !== right.id) return left.id.localeCompare(right.id);
    return (left.version ?? 0) - (right.version ?? 0);
  });
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

function findEntry(
  entries: readonly FlowCatalogEntry[],
  id: string,
): FlowCatalogEntry | undefined {
  return entries.find((entry) => entry.id === id);
}

function checkOwnedFunnelEntry(
  collector: IssueCollector,
  entries: readonly FlowCatalogEntry[],
  dependency: FlowDependencyRef,
  draft: FlowDraft,
  nodeId: string | undefined,
  label: string,
): void {
  const entry = findEntry(entries, dependency.id);
  if (!entry) {
    collector.error({
      code: "flow.dependency.not_found",
      message: `${label} "${dependency.id}" não existe no catálogo autorizado.`,
      recommendedAction: "Escolha um item existente ou recrie a configuração referenciada.",
      nodeId,
      dependency,
    });
    return;
  }
  if (entry.ownerId !== draft.ownerId) {
    collector.error({
      code: "flow.dependency.cross_owner",
      message: `${label} "${dependency.id}" pertence a outro proprietário.`,
      recommendedAction: "Remova a referência: um flow nunca atravessa proprietários.",
      nodeId,
      dependency,
    });
    return;
  }
  if (entry.funnelId !== undefined && entry.funnelId !== draft.funnelId) {
    collector.error({
      code: "flow.dependency.wrong_funnel",
      message: `${label} "${dependency.id}" pertence a outro funil.`,
      recommendedAction: "Use um item do funil deste flow; catálogos não vazam entre funis.",
      nodeId,
      dependency,
    });
    return;
  }
  if (entry.archived) {
    collector.warning({
      code: "flow.warning.dependency_archived",
      message: `${label} "${dependency.id}" está arquivado e pode sair dos seletores.`,
      recommendedAction: "Restaure o item ou aponte para um substituto antes de publicar.",
      nodeId,
      dependency,
    });
  }
}

function checkPublishedArtifact(
  collector: IssueCollector,
  artifacts: readonly FlowCatalogPublishedArtifact[],
  dependency: FlowDependencyRef,
  draft: FlowDraft,
  nodeId: string,
  label: string,
  parentId?: string,
): void {
  if (dependency.version === undefined || dependency.version < 1) {
    collector.error({
      code: "flow.dependency.version_not_pinned",
      message: `${label} "${dependency.id}" precisa fixar uma versão; "latest" não é reproduzível.`,
      recommendedAction: "Selecione a versão publicada exata que o flow deve usar.",
      nodeId,
      dependency,
    });
    return;
  }
  const artifact = artifacts.find((item) => item.id === dependency.id);
  if (!artifact) {
    collector.error({
      code: "flow.dependency.not_found",
      message: `${label} "${dependency.id}" não existe.`,
      recommendedAction: "Escolha um artefato publicado existente.",
      nodeId,
      dependency,
    });
    return;
  }
  if (artifact.ownerId !== draft.ownerId) {
    collector.error({
      code: "flow.dependency.cross_owner",
      message: `${label} "${dependency.id}" pertence a outro proprietário.`,
      recommendedAction: "Remova a referência: um flow nunca atravessa proprietários.",
      nodeId,
      dependency,
    });
    return;
  }
  if (artifact.status !== "published") {
    collector.error({
      code: "flow.dependency.not_published",
      message: `${label} "${dependency.id}" ainda é rascunho.`,
      recommendedAction: "Publique o artefato antes de referenciá-lo em um flow.",
      nodeId,
      dependency,
    });
    return;
  }
  if (artifact.version !== dependency.version) {
    collector.error({
      code: "flow.dependency.version_not_pinned",
      message: `${label} "${dependency.id}" está fixado na versão ${dependency.version}, mas a versão publicada é ${artifact.version}.`,
      recommendedAction: "Atualize a referência para a versão publicada correta.",
      nodeId,
      dependency,
    });
    return;
  }
  if (parentId !== undefined && artifact.parentId !== undefined && artifact.parentId !== parentId) {
    collector.error({
      code: "flow.dependency.not_found",
      message: `${label} "${dependency.id}" não pertence ao modelo informado.`,
      recommendedAction: "Selecione a versão do modelo correto.",
      nodeId,
      dependency,
    });
  }
}

function conditionValueProblem(
  valueType: FlowValueType,
  operator: FlowConditionOperator,
  value: FlowConditionLiteral | undefined,
): string | null {
  // Operadores sem valor são tratados uma única vez pelo chamador, com uma
  // mensagem específica. Evita duplicar o mesmo erro na lista acumulada.
  if (FLOW_VALUELESS_OPERATORS.includes(operator)) return null;
  if (value === undefined) return null;

  if (valueType === "set") {
    if (Array.isArray(value)) return "operadores de conjunto recebem um único item";
    return typeof value === "string" || typeof value === "number"
      ? null
      : "o item do conjunto precisa ser texto ou número";
  }

  if (FLOW_LIST_OPERATORS.includes(operator)) {
    if (!Array.isArray(value)) return null;
    return value.every((item) => typeof item === "string" || typeof item === "number")
      ? null
      : "a lista contém um item incompatível";
  }

  if (Array.isArray(value)) return "este operador exige um único valor";
  if (valueType === "text") return typeof value === "string" ? null : "texto era esperado";
  if (valueType === "boolean")
    return typeof value === "boolean" ? null : "verdadeiro ou falso era esperado";
  if (valueType === "number" || valueType === "duration")
    return typeof value === "number" && Number.isFinite(value) ? null : "número finito era esperado";
  if (valueType === "date")
    return typeof value === "string" && !Number.isNaN(Date.parse(value))
      ? null
      : "data válida em texto era esperada";
  if (valueType === "enum")
    return typeof value === "string" || typeof value === "number"
      ? null
      : "valor enumerado em texto ou número era esperado";
  return null;
}

function validateConditionExpression(
  collector: IssueCollector,
  condition: FlowCondition,
  edge: FlowEdge,
): void {
  if (flowConditionDepth(condition) > FLOW_CONDITION_MAX_DEPTH) {
    collector.error({
      code: "flow.condition.invalid_operator",
      message: `A condição da aresta "${edge.id}" excede a profundidade máxima de ${FLOW_CONDITION_MAX_DEPTH}.`,
      recommendedAction: "Divida a regra em condições menores ou em nós encadeados.",
      edgeId: edge.id,
    });
  }
  for (const comparison of flowConditionComparisons(condition)) {
    const valueType = operandValueType(comparison.operand);
    const key = operandKey(comparison.operand);
    if (!isOperatorAllowed(valueType, comparison.operator)) {
      collector.error({
        code: "flow.condition.invalid_operator",
        message: `O operador "${comparison.operator}" não se aplica ao tipo "${valueType}" em ${key}.`,
        recommendedAction: "Escolha um operador compatível com o tipo do operando.",
        edgeId: edge.id,
      });
      continue;
    }
    const needsValue = !FLOW_VALUELESS_OPERATORS.includes(comparison.operator);
    if (needsValue && comparison.value === undefined) {
      collector.error({
        code: "flow.condition.invalid_operator",
        message: `A comparação de ${key} usa "${comparison.operator}" sem valor.`,
        recommendedAction: "Informe o valor de comparação da condição.",
        edgeId: edge.id,
      });
    }
    if (FLOW_LIST_OPERATORS.includes(comparison.operator) && !Array.isArray(comparison.value)) {
      collector.error({
        code: "flow.condition.invalid_operator",
        message: `O operador "${comparison.operator}" em ${key} exige uma lista de valores.`,
        recommendedAction: "Informe a lista de valores aceitos.",
        edgeId: edge.id,
      });
    }
    if (!needsValue && comparison.value !== undefined) {
      collector.error({
        code: "flow.condition.invalid_value",
        message: `O operador "${comparison.operator}" em ${key} não aceita valor de comparação.`,
        recommendedAction: "Remova o valor; esse operador avalia apenas presença ou vazio.",
        edgeId: edge.id,
      });
    }
    const valueProblem = conditionValueProblem(
      valueType,
      comparison.operator,
      comparison.value,
    );
    if (valueProblem) {
      collector.error({
        code: "flow.condition.invalid_value",
        message: `O valor informado em ${key} é inválido: ${valueProblem}.`,
        recommendedAction: "Use um valor compatível com o tipo e o operador da condição.",
        edgeId: edge.id,
      });
    }
  }
}

function comparisonValues(value: FlowConditionLiteral | undefined): readonly (string | number | boolean)[] {
  if (value === undefined) return [];
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
    return [value];
  return value;
}

function validateAllowedConditionValues(
  collector: IssueCollector,
  edge: FlowEdge,
  label: string,
  values: readonly (string | number | boolean)[],
  allowed: readonly string[],
): void {
  for (const value of values) {
    if (typeof value !== "string" || !allowed.includes(value)) {
      collector.error({
        code: "flow.condition.invalid_value",
        message: `O valor "${String(value)}" não existe em ${label}.`,
        recommendedAction: "Escolha um valor publicado e permitido pelo catálogo da condição.",
        edgeId: edge.id,
      });
    }
  }
}

function subflowDepth(
  catalog: FlowDependencyCatalog,
  versionId: string,
  seenVersions: Set<string>,
  seenFlows: Set<string>,
): { depth: number; recursive: boolean } {
  if (seenVersions.has(versionId)) return { depth: 0, recursive: true };
  const version = catalog.flowVersions.find((item) => item.id === versionId);
  if (!version) return { depth: 0, recursive: false };
  if (seenFlows.has(version.flowId)) return { depth: 0, recursive: true };
  const nextVersions = new Set(seenVersions).add(versionId);
  const nextFlows = new Set(seenFlows).add(version.flowId);
  let depth = 1;
  let recursive = false;
  for (const node of version.snapshot.nodes) {
    if (node.config.type !== "call_flow") continue;
    const child = subflowDepth(
      catalog,
      node.config.target.flowVersionId,
      nextVersions,
      nextFlows,
    );
    recursive = recursive || child.recursive;
    depth = Math.max(depth, 1 + child.depth);
  }
  return { depth, recursive };
}

export interface ValidateFlowDraftOptions {
  /** Capabilities externas continuam desligadas neste lote; gera avisos. */
  externalActionsEnabled?: boolean;
}

export function validateFlowDraft(
  draft: FlowDraft,
  catalog: FlowDependencyCatalog,
  options: ValidateFlowDraftOptions = {},
): FlowValidationResult {
  const collector = new IssueCollector();
  const simple = draft.structureType === "simple";

  if (draft.ownerId !== catalog.ownerId) {
    collector.error({
      code: "flow.owner.mismatch",
      message: "O rascunho e o catálogo autorizado pertencem a proprietários diferentes.",
      recommendedAction: "Recarregue o rascunho no contexto do proprietário autenticado.",
    });
  }
  const funnel = catalog.funnels.find((item) => item.id === draft.funnelId);
  if (!funnel) {
    collector.error({
      code: "flow.dependency.not_found",
      message: `O funil "${draft.funnelId}" do rascunho não existe no catálogo autorizado.`,
      recommendedAction: "Escolha um funil existente antes de validar ou compilar.",
      dependency: { type: "funnel", id: draft.funnelId },
    });
  } else {
    if (funnel.ownerId !== draft.ownerId) {
      collector.error({
        code: "flow.dependency.cross_owner",
        message: `O funil "${draft.funnelId}" pertence a outro proprietário.`,
        recommendedAction: "Remova a referência cruzada entre proprietários.",
        dependency: { type: "funnel", id: draft.funnelId },
      });
    }
    if (funnel.structureType !== draft.structureType) {
      collector.error({
        code: "flow.structure.mismatch",
        message: `O rascunho é "${draft.structureType}", mas o funil está configurado como "${funnel.structureType}".`,
        recommendedAction: "Use a estrutura do funil; conversão simple → graph cria uma cópia separada.",
        dependency: { type: "funnel", id: draft.funnelId },
      });
    }
    if (funnel.archived) {
      collector.warning({
        code: "flow.warning.dependency_archived",
        message: `O funil "${draft.funnelId}" está arquivado.`,
        recommendedAction: "Restaure o funil antes de publicar novas definições.",
        dependency: { type: "funnel", id: draft.funnelId },
      });
    }
  }

  // --- Políticas obrigatórias -------------------------------------------------
  if (!FLOW_REPLY_POLICIES.includes(draft.replyPolicy)) {
    collector.error({
      code: "flow.reply_policy.missing",
      message: "O flow não declara o que acontece quando o lead responde.",
      recommendedAction: 'Escolha "pausar", "rotear por categoria" ou "continuar e notificar".',
    });
  } else if (draft.replyPolicy === "continue_and_notify") {
    collector.warning({
      code: "flow.warning.reply_continue_and_notify",
      message:
        "O flow continuará mesmo depois de uma resposta do lead, apenas notificando.",
      recommendedAction:
        "Confirme essa escolha na publicação futura; sinais de segurança e supressão sempre prevalecem.",
    });
  }

  if (draft.entryPolicy === "domain_event") {
    const trigger = draft.entryDomainEvent;
    if (!trigger || !(FLOW_DOMAIN_EVENT_TRIGGERS as readonly string[]).includes(trigger)) {
      collector.error({
        code: "flow.entry.invalid_trigger",
        message: "A entrada por evento de domínio exige um gatilho da allowlist.",
        recommendedAction: "Escolha um evento permitido; trigger arbitrário não existe na v1.",
      });
    }
  }

  // --- Identidade de nós e arestas -------------------------------------------
  const nodeIds = new Set<string>();
  for (const node of draft.nodes) {
    if (nodeIds.has(node.id)) {
      collector.error({
        code: "flow.node.duplicated_id",
        message: `O nó "${node.id}" está duplicado.`,
        recommendedAction: "Gere um identificador único para cada nó.",
        nodeId: node.id,
      });
    }
    nodeIds.add(node.id);
  }
  const edgeIds = new Set<string>();
  for (const edge of draft.edges) {
    if (edgeIds.has(edge.id)) {
      collector.error({
        code: "flow.edge.duplicated_id",
        message: `A aresta "${edge.id}" está duplicada.`,
        recommendedAction: "Gere um identificador único para cada aresta.",
        edgeId: edge.id,
      });
    }
    edgeIds.add(edge.id);
  }

  // --- Início e terminais ----------------------------------------------------
  const startNodes = draft.nodes.filter((node) => node.type === "start");
  if (startNodes.length === 0) {
    collector.error({
      code: "flow.start.missing",
      message: "O flow não possui nó de início.",
      recommendedAction: "Adicione exatamente um nó de início.",
    });
  }
  if (startNodes.length > 1) {
    collector.error({
      code: "flow.start.multiple",
      message: `O flow possui ${startNodes.length} nós de início; a v1 aceita exatamente um.`,
      recommendedAction: "Mantenha um único início; entradas paralelas não existem na v1.",
    });
  }
  const terminals = draft.nodes.filter((node) => isFlowTerminalNode(node.type));
  if (terminals.length === 0) {
    collector.error({
      code: "flow.terminal.missing",
      message: "O flow não possui nenhum término (ganho, perda ou fim).",
      recommendedAction: "Adicione ao menos um término alcançável.",
    });
  }

  const index = buildFlowGraphIndex(draft.nodes, draft.edges);

  // --- Arestas: destino, porta e determinismo --------------------------------
  for (const edge of draft.edges) {
    const from = index.nodeById.get(edge.fromNodeId);
    const to = index.nodeById.get(edge.toNodeId);
    if (!from || !to) {
      collector.error({
        code: "flow.edge.dangling",
        message: `A aresta "${edge.id}" aponta para um nó inexistente.`,
        recommendedAction: "Ligue a aresta a nós existentes ou remova-a.",
        edgeId: edge.id,
      });
      continue;
    }
    if (edge.fromNodeId === edge.toNodeId) {
      collector.error({
        code: "flow.edge.self_reference",
        message: `A aresta "${edge.id}" liga o nó "${edge.fromNodeId}" a ele mesmo.`,
        recommendedAction: "Um ciclo precisa passar por espera ou tarefa humana.",
        edgeId: edge.id,
      });
    }
    const allowedPorts = flowNodeOutputPorts[from.type];
    if (!allowedPorts.includes(edge.fromPort)) {
      collector.error({
        code: "flow.edge.port_incompatible",
        message: `A porta "${edge.fromPort}" não existe no nó "${from.id}" do tipo "${from.type}".`,
        recommendedAction: "Use uma porta válida para o tipo do nó de origem.",
        edgeId: edge.id,
        nodeId: from.id,
      });
    }
    if (edge.fallback && edge.fromPort !== "fallback") {
      collector.error({
        code: "flow.edge.port_incompatible",
        message: `A aresta "${edge.id}" está marcada como padrão fora da porta de fallback.`,
        recommendedAction: 'Use a porta "fallback" para a saída padrão.',
        edgeId: edge.id,
      });
    }
    if (edge.fromPort === "fallback" && !edge.fallback) {
      collector.error({
        code: "flow.edge.port_incompatible",
        message: `A aresta "${edge.id}" usa a porta de fallback sem se declarar como padrão.`,
        recommendedAction: "Marque a aresta como saída padrão ou mude a porta.",
        edgeId: edge.id,
      });
    }
    if (edge.fromPort === "match" && !edge.condition) {
      collector.error({
        code: "flow.condition.missing_expression",
        message: `A aresta condicional "${edge.id}" não possui expressão.`,
        recommendedAction: "Defina a condição tipada dessa saída.",
        edgeId: edge.id,
      });
    }
    if (edge.condition && edge.fromPort !== "match") {
      collector.error({
        code: "flow.edge.port_incompatible",
        message: `A aresta "${edge.id}" declara condição fora de uma saída condicional.`,
        recommendedAction: "Mova a condição para uma aresta na porta de condição.",
        edgeId: edge.id,
      });
    }
    if (edge.condition) validateConditionExpression(collector, edge.condition, edge);
  }

  // --- Saídas por nó ---------------------------------------------------------
  for (const node of index.nodes) {
    const edges = outgoingEdges(index, node.id);
    const branching = FLOW_BRANCHING_NODE_TYPES.includes(node.type);

    if (!branching && node.type !== "call_flow" && edges.length > 1) {
      collector.error({
        code: "flow.parallelism.implicit",
        message: `O nó "${node.id}" possui ${edges.length} saídas simultâneas.`,
        recommendedAction:
          "A v1 escolhe exatamente um caminho: use condição ou teste A/B para ramificar.",
        nodeId: node.id,
      });
    }

    const byPriority = new Map<string, number>();
    for (const edge of edges) {
      const key = `${edge.fromPort}:${edge.priority}`;
      byPriority.set(key, (byPriority.get(key) ?? 0) + 1);
    }
    for (const [key, count] of byPriority) {
      if (count > 1) {
        collector.error({
          code: "flow.edge.ambiguous_priority",
          message: `O nó "${node.id}" possui ${count} saídas com a mesma prioridade em "${key.split(":")[0]}".`,
          recommendedAction: "Dê prioridades distintas para tornar a avaliação determinística.",
          nodeId: node.id,
        });
      }
    }

    if (node.type === "condition") {
      const matches = edges.filter((edge) => edge.fromPort === "match");
      const fallbacks = edges.filter((edge) => edge.fromPort === "fallback");
      if (fallbacks.length > 1) {
        collector.error({
          code: "flow.condition.multiple_fallback",
          message: `O nó condicional "${node.id}" possui ${fallbacks.length} saídas padrão.`,
          recommendedAction: "Mantenha no máximo uma saída padrão por condição.",
          nodeId: node.id,
        });
      }
      if (matches.length === 0 || fallbacks.length === 0) {
        collector.error({
          code: "flow.condition.no_valid_output",
          message: `O nó condicional "${node.id}" precisa de ao menos uma saída condicional e uma saída padrão.`,
          recommendedAction:
            "Explique o que acontece quando nenhuma condição corresponde adicionando a saída padrão.",
          nodeId: node.id,
        });
      }
      const fallback = fallbacks[0];
      if (fallback) {
        for (const match of matches) {
          if (match.priority > fallback.priority) {
            collector.warning({
              code: "flow.warning.unlikely_path",
              message: `A saída "${match.id}" é avaliada depois da saída padrão e dificilmente será usada.`,
              recommendedAction: "Dê à saída padrão a maior prioridade numérica do nó.",
              edgeId: match.id,
              nodeId: node.id,
            });
          }
        }
      }
    }

    if (node.type === "random_split" && node.config.type === "random_split") {
      const variants = node.config.variants;
      const total = variants.reduce((sum, variant) => sum + variant.weight, 0);
      const invalid =
        total !== FLOW_SPLIT_TOTAL_WEIGHT ||
        variants.some((variant) => !Number.isInteger(variant.weight) || variant.weight < 0);
      if (invalid) {
        collector.error({
          code: "flow.split.invalid_weights",
          message: `Os pesos do teste "${node.id}" somam ${total}; eles precisam ser inteiros não negativos somando ${FLOW_SPLIT_TOTAL_WEIGHT}.`,
          recommendedAction: "Ajuste os pesos das variantes.",
          nodeId: node.id,
        });
      }
      const keys = new Set<string>();
      for (const variant of variants) {
        if (keys.has(variant.key)) {
          collector.error({
            code: "flow.node.invalid_config",
            message: `A variante "${variant.key}" está duplicada no teste "${node.id}".`,
            recommendedAction: "Use uma chave única por variante.",
            nodeId: node.id,
          });
        }
        keys.add(variant.key);
      }
      for (const variant of variants) {
        const mapped = edges.filter((edge) => edge.variantKey === variant.key);
        if (mapped.length !== 1) {
          collector.error({
            code: "flow.split.unmapped_variant",
            message: `A variante "${variant.key}" do nó "${node.id}" precisa de exatamente uma saída.`,
            recommendedAction: "Ligue cada variante a um caminho distinto.",
            nodeId: node.id,
          });
        }
      }
      for (const edge of edges) {
        if (!edge.variantKey || !keys.has(edge.variantKey)) {
          collector.error({
            code: "flow.split.unmapped_variant",
            message: `A saída "${edge.id}" não aponta para uma variante declarada.`,
            recommendedAction: "Associe a saída a uma variante existente.",
            edgeId: edge.id,
            nodeId: node.id,
          });
        }
      }
    }

    if (node.type === "call_flow" && node.config.type === "call_flow") {
      const target = node.config.target;
      const transfer = target.scope === "same_funnel" && target.semantics === "transfer";
      if (transfer && edges.length > 0) {
        collector.error({
          code: "flow.subflow.transfer_with_return",
          message: `A transferência do nó "${node.id}" não pode ter saída de retorno.`,
          recommendedAction: "Use a semântica de chamada com retorno ou remova a saída.",
          nodeId: node.id,
        });
      }
      if (!transfer && edges.length !== 1) {
        collector.error({
          code: "flow.node.invalid_config",
          message: `A chamada do nó "${node.id}" precisa de exatamente uma saída de retorno.`,
          recommendedAction: "Ligue a porta de retorno a um único caminho.",
          nodeId: node.id,
        });
      }
    }

    if (isFlowTerminalNode(node.type) && edges.length > 0) {
      collector.error({
        code: "flow.edge.port_incompatible",
        message: `O término "${node.id}" não pode possuir saída.`,
        recommendedAction: "Remova as arestas que saem de um término.",
        nodeId: node.id,
      });
    }

    if (node.type === "end" && node.config.type === "end" && !node.config.outcomeNote) {
      collector.warning({
        code: "flow.warning.terminal_without_objective",
        message: `O término "${node.id}" não explica o objetivo alcançado.`,
        recommendedAction: "Descreva o desfecho para tornar a run explicável.",
        nodeId: node.id,
      });
    }
  }

  // --- Alcançabilidade -------------------------------------------------------
  const startNode = startNodes.length === 1 ? startNodes[0] : undefined;
  if (startNode) {
    const reachable = reachableFrom(index, startNode.id);
    for (const node of index.nodes) {
      if (!reachable.has(node.id)) {
        collector.error({
          code: "flow.node.unreachable",
          message: `O nó "${node.id}" não é alcançável a partir do início.`,
          recommendedAction: "Ligue o nó ao fluxo ou remova-o.",
          nodeId: node.id,
        });
      }
    }
    if (terminals.length > 0 && !terminals.some((terminal) => reachable.has(terminal.id))) {
      collector.error({
        code: "flow.terminal.unreachable",
        message: "Nenhum término é alcançável a partir do início.",
        recommendedAction: "Garanta um caminho do início até ganho, perda ou fim.",
      });
    }

    // --- Ciclos --------------------------------------------------------------
    const cycles = findFlowCycles(index, startNode.id);
    const cyclesAlreadyReportedWithoutWait = new Set<string>();
    for (const cycle of cycles) {
      const cycleNodes = cycle.nodeIds
        .map((id) => index.nodeById.get(id))
        .filter((node): node is FlowNode => Boolean(node));
      const hasPause = cycleNodes.some((node) => node.type === "wait" || node.type === "human_task");
      if (!hasPause) {
        cyclesAlreadyReportedWithoutWait.add(cycle.backEdgeId);
        collector.error({
          code: "flow.cycle.without_wait",
          message: `O ciclo que passa por "${cycle.nodeIds.join(" → ")}" não possui espera nem tarefa humana.`,
          recommendedAction: "Insira uma espera ou tarefa humana dentro do ciclo.",
          edgeId: cycle.backEdgeId,
        });
      }
      const cycleSet = new Set(cycle.nodeIds);
      const hasExit = cycle.nodeIds.some((nodeId) =>
        outgoingEdges(index, nodeId).some(
          (edge) => !cycleSet.has(edge.toNodeId) && reachesTerminal(index, edge.toNodeId),
        ),
      );
      if (!hasExit) {
        collector.error({
          code: "flow.cycle.without_exit",
          message: `O ciclo que passa por "${cycle.nodeIds.join(" → ")}" não possui saída que alcance um término.`,
          recommendedAction: "Adicione uma saída do ciclo em direção a um término.",
          edgeId: cycle.backEdgeId,
        });
      }
      const backEdge = draft.edges.find((edge) => edge.id === cycle.backEdgeId);
      if (backEdge && backEdge.loopLimit === undefined) {
        collector.error({
          code: "flow.cycle.without_limit",
          message: `A aresta "${cycle.backEdgeId}" fecha um ciclo sem limite de iterações.`,
          recommendedAction: `Defina um limite inteiro entre ${FLOW_LOOP_LIMIT_MIN} e ${FLOW_LOOP_LIMIT_MAX}.`,
          edgeId: cycle.backEdgeId,
        });
      }
      if (
        backEdge?.loopLimit !== undefined &&
        (!Number.isInteger(backEdge.loopLimit) ||
          backEdge.loopLimit < FLOW_LOOP_LIMIT_MIN ||
          backEdge.loopLimit > FLOW_LOOP_LIMIT_MAX)
      ) {
        collector.error({
          code: "flow.cycle.limit_out_of_range",
          message: `O limite ${backEdge.loopLimit} da aresta "${cycle.backEdgeId}" está fora de ${FLOW_LOOP_LIMIT_MIN}–${FLOW_LOOP_LIMIT_MAX}.`,
          recommendedAction: `Use um inteiro entre ${FLOW_LOOP_LIMIT_MIN} e ${FLOW_LOOP_LIMIT_MAX}.`,
          edgeId: cycle.backEdgeId,
        });
      }
      if (simple) {
        collector.error({
          code: "flow.simple.forbidden_branch",
          message: "O funil simples é linear e não aceita ciclo.",
          recommendedAction: "Crie um fluxograma separado para representar o retorno.",
          edgeId: cycle.backEdgeId,
        });
      }
    }

    // Uma DFS tradicional registra apenas um caminho para cada back edge. Em
    // grafos convergentes, esse caminho pode conter uma espera enquanto outro
    // ciclo que usa a mesma aresta não contém. Remover os nós de pausa e buscar
    // ciclos no subgrafo restante fecha essa alternativa perigosa.
    const nonPauseNodes = index.nodes.filter(
      (node) =>
        reachable.has(node.id) && node.type !== "wait" && node.type !== "human_task",
    );
    const nonPauseIds = new Set(nonPauseNodes.map((node) => node.id));
    const nonPauseIndex = buildFlowGraphIndex(
      nonPauseNodes,
      index.edges.filter(
        (edge) => nonPauseIds.has(edge.fromNodeId) && nonPauseIds.has(edge.toNodeId),
      ),
    );
    const pauseFreeCycles = new Map<string, ReturnType<typeof findFlowCycles>[number]>();
    for (const node of nonPauseNodes) {
      for (const cycle of findFlowCycles(nonPauseIndex, node.id))
        pauseFreeCycles.set(cycle.backEdgeId, cycle);
    }
    for (const cycle of pauseFreeCycles.values()) {
      if (cyclesAlreadyReportedWithoutWait.has(cycle.backEdgeId)) continue;
      collector.error({
        code: "flow.cycle.without_wait",
        message: `Existe um caminho cíclico por "${cycle.nodeIds.join(" → ")}" sem espera nem tarefa humana.`,
        recommendedAction: "Garanta que todo caminho de retorno atravesse uma espera ou tarefa humana.",
        edgeId: cycle.backEdgeId,
      });
    }
  }

  // --- Restrições do funil simples -------------------------------------------
  if (simple) {
    for (const node of draft.nodes) {
      if (!SIMPLE_FLOW_ALLOWED_NODE_TYPES.includes(node.type)) {
        collector.error({
          code: "flow.simple.forbidden_node",
          message: `O nó "${node.id}" do tipo "${node.type}" não existe no funil simples.`,
          recommendedAction: "Crie um fluxograma para usar condição, teste A/B, IA ou subfluxo.",
          nodeId: node.id,
        });
      }
    }
    for (const node of index.nodes) {
      if (outgoingEdges(index, node.id).length > 1) {
        collector.error({
          code: "flow.simple.forbidden_branch",
          message: `O nó "${node.id}" ramifica em mais de um caminho.`,
          recommendedAction: "O funil simples é uma sequência linear publicada.",
          nodeId: node.id,
        });
      }
    }
  }

  // --- Dependências ----------------------------------------------------------
  for (const node of draft.nodes) {
    const config = node.config;
    if (node.stageId) {
      checkOwnedFunnelEntry(
        collector,
        catalog.stages,
        { type: "stage", id: node.stageId, funnelId: draft.funnelId },
        draft,
        node.id,
        "A etapa",
      );
    }
    if (config.type === "stage") {
      checkOwnedFunnelEntry(
        collector,
        catalog.stages,
        { type: "stage", id: config.stageId, funnelId: draft.funnelId },
        draft,
        node.id,
        "A etapa",
      );
    }
    if (config.type === "lost") {
      if (!config.lossReasonId || !config.stageId || !config.evidence.trim()) {
        collector.error({
          code: "flow.lost.missing_requirements",
          message: `A perda automática do nó "${node.id}" exige motivo, etapa e evidência.`,
          recommendedAction: "Preencha motivo, etapa e evidência antes de publicar.",
          nodeId: node.id,
        });
      }
      checkOwnedFunnelEntry(
        collector,
        catalog.lossReasons,
        { type: "loss_reason", id: config.lossReasonId, funnelId: draft.funnelId },
        draft,
        node.id,
        "O motivo de perda",
      );
      checkOwnedFunnelEntry(
        collector,
        catalog.stages,
        { type: "stage", id: config.stageId, funnelId: draft.funnelId },
        draft,
        node.id,
        "A etapa da perda",
      );
    }
    if (config.type === "set_field_or_tag") {
      const assignmentKeys = new Set<string>();
      for (const assignment of config.assignments) {
        const assignmentKey = `${assignment.target}:${assignment.id}`;
        if (assignmentKeys.has(assignmentKey)) {
          collector.error({
            code: "flow.node.invalid_config",
            message: `O nó "${node.id}" altera "${assignmentKey}" mais de uma vez.`,
            recommendedAction: "Mantenha uma única operação por campo, tag ou gatilho no mesmo nó.",
            nodeId: node.id,
          });
        }
        assignmentKeys.add(assignmentKey);
        const entries =
          assignment.target === "lead_field"
            ? catalog.leadFields
            : assignment.target === "tag"
              ? catalog.tags
              : catalog.triggers;
        const label =
          assignment.target === "lead_field"
            ? "O campo"
            : assignment.target === "tag"
              ? "A tag"
              : "O gatilho";
        checkOwnedFunnelEntry(
          collector,
          entries,
          {
            type:
              assignment.target === "lead_field"
                ? "lead_field"
                : assignment.target === "tag"
                  ? "tag"
                  : "trigger",
            id: assignment.id,
            funnelId: draft.funnelId,
          },
          draft,
          node.id,
          label,
        );
        if (assignment.operation === "set" && assignment.value === undefined) {
          collector.error({
            code: "flow.node.invalid_config",
            message: `A atribuição de "${assignment.id}" no nó "${node.id}" não informa valor.`,
            recommendedAction: 'Informe o valor ou use a operação "limpar".',
            nodeId: node.id,
          });
        }
        const fieldOperationValid =
          assignment.target === "lead_field" &&
          (assignment.operation === "set" || assignment.operation === "clear");
        const setOperationValid =
          assignment.target !== "lead_field" &&
          (assignment.operation === "add" || assignment.operation === "remove");
        if (!fieldOperationValid && !setOperationValid) {
          collector.error({
            code: "flow.node.invalid_config",
            message: `A operação "${assignment.operation}" não é válida para "${assignment.target}".`,
            recommendedAction: "Use set/clear em campos e add/remove em tags ou gatilhos.",
            nodeId: node.id,
          });
        }
        if (assignment.operation !== "set" && assignment.value !== undefined) {
          collector.error({
            code: "flow.node.invalid_config",
            message: `A operação "${assignment.operation}" em "${assignment.id}" não aceita valor.`,
            recommendedAction: "Remova o valor ou use a operação set em um campo.",
            nodeId: node.id,
          });
        }
      }
    }
    if (config.type === "send_message") {
      checkPublishedArtifact(
        collector,
        catalog.messageTemplateVersions,
        {
          type: "message_template_version",
          id: config.templateVersionId,
          version: config.templateVersion,
        },
        draft,
        node.id,
        "O modelo de mensagem",
        config.templateId,
      );
      if (!options.externalActionsEnabled) {
        collector.warning({
          code: "flow.warning.external_action_disabled",
          message: `O envio do nó "${node.id}" depende do canal de e-mail, que continua desabilitado.`,
          recommendedAction: "Nenhuma mensagem sai enquanto a capability de envio for falsa.",
          nodeId: node.id,
        });
      }
    }
    if (config.type === "ai_response") {
      checkPublishedArtifact(
        collector,
        catalog.aiPromptVersions,
        { type: "ai_prompt_version", id: config.promptVersionId, version: config.promptVersion },
        draft,
        node.id,
        "O prompt",
      );
      if (!options.externalActionsEnabled) {
        collector.warning({
          code: "flow.warning.external_action_disabled",
          message: `A geração do nó "${node.id}" depende da assistência de IA, que continua desabilitada.`,
          recommendedAction: "Nenhuma geração ocorre enquanto a capability de IA for falsa.",
          nodeId: node.id,
        });
      }
    }
    if (config.type === "call_flow") {
      const target = config.target;
      const dependency: FlowDependencyRef = {
        type: "flow_version",
        id: target.flowVersionId,
        version: target.flowVersion,
      };
      if (catalog.flowDraftIds.includes(target.flowVersionId)) {
        collector.error({
          code: "flow.dependency.not_published",
          message: `O nó "${node.id}" chama um rascunho; somente versão publicada pode ser chamada.`,
          recommendedAction: "Publique o flow de destino e fixe a versão.",
          nodeId: node.id,
          dependency,
        });
      } else {
        const version = catalog.flowVersions.find((item) => item.id === target.flowVersionId);
        if (!version) {
          collector.error({
            code: "flow.dependency.not_found",
            message: `A versão de flow "${target.flowVersionId}" não existe.`,
            recommendedAction: "Escolha uma versão publicada existente.",
            nodeId: node.id,
            dependency,
          });
        } else if (version.ownerId !== draft.ownerId) {
          collector.error({
            code: "flow.dependency.cross_owner",
            message: `A versão de flow "${target.flowVersionId}" pertence a outro proprietário.`,
            recommendedAction: "Remova a referência: um flow nunca atravessa proprietários.",
            nodeId: node.id,
            dependency,
          });
        } else if (version.version !== target.flowVersion) {
          collector.error({
            code: "flow.dependency.version_not_pinned",
            message: `A chamada do nó "${node.id}" fixa a versão ${target.flowVersion}, mas a versão publicada é ${version.version}.`,
            recommendedAction: "Atualize a referência para a versão correta.",
            nodeId: node.id,
            dependency,
          });
        } else {
          if (version.flowId === draft.id) {
            collector.error({
              code: "flow.subflow.recursive",
              message: `O nó "${node.id}" chama o próprio flow.`,
              recommendedAction: "Recursão direta é proibida; use um flow diferente.",
              nodeId: node.id,
              dependency,
            });
          }
          const chain =
            version.flowId === draft.id
              ? { depth: 0, recursive: true }
              : subflowDepth(catalog, version.id, new Set(), new Set([draft.id]));
          if (chain.recursive && version.flowId !== draft.id) {
            collector.error({
              code: "flow.subflow.recursive",
              message: `A cadeia de subfluxos a partir do nó "${node.id}" é recursiva.`,
              recommendedAction: "Quebre o ciclo entre os flows chamados.",
              nodeId: node.id,
              dependency,
            });
          } else if (chain.depth + 1 > FLOW_MAX_SUBFLOW_DEPTH) {
            collector.error({
              code: "flow.subflow.depth_exceeded",
              message: `A cadeia de subfluxos a partir do nó "${node.id}" tem profundidade ${chain.depth + 1}, acima do limite ${FLOW_MAX_SUBFLOW_DEPTH}.`,
              recommendedAction: "Reduza a cadeia de chamadas entre flows.",
              nodeId: node.id,
              dependency,
            });
          }
          if (target.scope === "same_funnel" && version.funnelId !== draft.funnelId) {
            collector.error({
              code: "flow.dependency.wrong_funnel",
              message: `A chamada do nó "${node.id}" declara o mesmo funil, mas a versão pertence a outro funil.`,
              recommendedAction: "Use a chamada entre funis para alcançar outro funil.",
              nodeId: node.id,
              dependency,
            });
          }
          if (target.scope === "other_funnel") {
            if (version.funnelId !== target.funnelId) {
              collector.error({
                code: "flow.dependency.wrong_funnel",
                message: `A versão "${version.id}" não pertence ao funil "${target.funnelId}".`,
                recommendedAction: "Escolha uma versão publicada do funil de destino.",
                nodeId: node.id,
                dependency,
              });
            }
            if (target.funnelId === draft.funnelId) {
              collector.error({
                code: "flow.dependency.wrong_funnel",
                message: `O nó "${node.id}" declara outro funil, mas aponta para o próprio funil.`,
                recommendedAction: "Use a chamada dentro do mesmo funil.",
                nodeId: node.id,
                dependency,
              });
            }
            checkOwnedFunnelEntry(
              collector,
              catalog.funnels,
              { type: "funnel", id: target.funnelId },
              draft,
              node.id,
              "O funil de destino",
            );
          }
        }
      }
    }
  }

  // --- Catálogos versionados citados por condições ---------------------------
  // Reaproveita os catálogos já versionados pelo Lote 5; nenhum catálogo
  // equivalente é criado aqui.
  for (const edge of draft.edges) {
    if (!edge.condition) continue;
    for (const comparison of flowConditionComparisons(edge.condition)) {
      const operand = comparison.operand;
      const values = comparisonValues(comparison.value);
      if (operand.kind === "reply_classification") {
        checkPublishedArtifact(
          collector,
          catalog.replyClassificationVersions,
          {
            type: "reply_classification_version",
            id: operand.catalogVersionId,
            version: operand.catalogVersion,
          },
          draft,
          edge.fromNodeId,
          "O catálogo de classificações",
        );
        const version = catalog.replyClassificationVersions.find(
          (item) => item.id === operand.catalogVersionId && item.version === operand.catalogVersion,
        );
        if (version?.allowedValues)
          validateAllowedConditionValues(
            collector,
            edge,
            "o catálogo publicado de classificações",
            values,
            version.allowedValues,
          );
      }
      if (operand.kind === "approach_type") {
        checkPublishedArtifact(
          collector,
          catalog.approachTypeVersions,
          {
            type: "approach_type_version",
            id: operand.catalogVersionId,
            version: operand.catalogVersion,
          },
          draft,
          edge.fromNodeId,
          "O catálogo de abordagens",
        );
        const version = catalog.approachTypeVersions.find(
          (item) => item.id === operand.catalogVersionId && item.version === operand.catalogVersion,
        );
        if (version?.allowedValues)
          validateAllowedConditionValues(
            collector,
            edge,
            "o catálogo publicado de abordagens",
            values,
            version.allowedValues,
          );
      }
      if (operand.kind === "custom_field") {
        checkOwnedFunnelEntry(
          collector,
          catalog.leadFields,
          { type: "lead_field", id: operand.fieldId, funnelId: draft.funnelId },
          draft,
          edge.fromNodeId,
          "O campo do formulário",
        );
      }
      if (operand.kind === "related_membership") {
        checkOwnedFunnelEntry(
          collector,
          catalog.funnels,
          { type: "funnel", id: operand.funnelId },
          draft,
          edge.fromNodeId,
          "O funil relacionado",
        );
      }
      if (operand.kind === "stage" || operand.kind === "tag" || operand.kind === "trigger") {
        const entries =
          operand.kind === "stage"
            ? catalog.stages
            : operand.kind === "tag"
              ? catalog.tags
              : catalog.triggers;
        const type = operand.kind === "stage" ? "stage" : operand.kind;
        const label = operand.kind === "stage" ? "A etapa" : operand.kind === "tag" ? "A tag" : "O gatilho";
        for (const value of values) {
          checkOwnedFunnelEntry(
            collector,
            entries,
            { type, id: String(value), funnelId: draft.funnelId },
            draft,
            edge.fromNodeId,
            label,
          );
        }
      }
      if (operand.kind === "membership_status" || operand.kind === "related_membership") {
        validateAllowedConditionValues(
          collector,
          edge,
          "os estados de participação",
          values,
          ["active", "won", "lost"],
        );
      }
      if (operand.kind === "base_field" && operand.field === "priority") {
        validateAllowedConditionValues(
          collector,
          edge,
          "as prioridades do Lead",
          values,
          ["low", "medium", "high"],
        );
      }
    }
  }

  return collector.result();
}
